import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, AlertCircle, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OnboardingShell } from "@/components/shells/OnboardingShell";

// ONB-01 — Onboarding status / progress
const SCENARIOS = {
  PendingReview:           { stage: "application", title: "Application under review", body: "We review applications in the order received. Most decisions land within two business days. We&rsquo;ll email you the moment a decision is made." },
  ApprovedAwaitingKYC:     { stage: "kyc",         title: "Verify your identity",     body: "Your application was approved. Next, complete a 2-minute identity check by Veriff &mdash; selfie plus a photo of your government ID.", action: { label: "Start KYC", to: "/onboarding/kyc" } },
  KYCInProgress:           { stage: "kyc",         title: "Identity check in progress", body: "Veriff is reviewing your submission. Most return a verdict in under 10 minutes. You&rsquo;ll get an email when it&rsquo;s done." },
  KYCPassAwaitingBank:     { stage: "bank",        title: "Add a bank account",       body: "Identity verified. Next, capture the bank account where we&rsquo;ll send your ACH payouts. We encrypt this at rest and only Payments Overseers can decrypt it for export.", action: { label: "Add bank account", to: "/onboarding/bank" } },
  BankCapturedAwaitingTerms: { stage: "terms",     title: "Accept membership terms",  body: "Bank captured. The final step is to accept the membership terms. After that, you&rsquo;re free to reserve any open deal.", action: { label: "Review &amp; accept terms", to: "/onboarding/terms" } },
  KYCFailed:               { stage: "kyc",         title: "Identity check needs another try", body: "Veriff couldn&rsquo;t verify your ID this time &mdash; usually a low-resolution photo. Retry in good light, holding the ID flat.", tone: "danger", action: { label: "Retry KYC", to: "/onboarding/kyc" } },
  ActiveMember:            { stage: "active",      title: "You&rsquo;re an active member", body: "You can browse and reserve any open deal.", action: { label: "Go to dashboard", to: "/member/dashboard" } },
};

export default function OnboardingStatusPage() {
  const scenarioKey = "ApprovedAwaitingKYC";
  const scenario = SCENARIOS[scenarioKey];

  return (
    <OnboardingShell currentStage={scenario.stage}>
      <div className="eyebrow mb-3 text-brand-700">Current step</div>
      <h1 className="font-brand text-[44px] lg:text-[48px] leading-[1.05] tracking-[-0.025em] text-ink font-bold"
          dangerouslySetInnerHTML={{ __html: scenario.title }} />
      <p className="mt-6 text-body text-ink-muted leading-relaxed max-w-xl"
         dangerouslySetInnerHTML={{ __html: scenario.body }} />

      {scenario.action && (
        <div className="mt-9">
          <Link to={scenario.action.to}>
            <Button className="bg-brand-700 hover:bg-brand-800 text-white h-12 px-7 text-body-sm font-semibold group" data-testid="onboarding-cta">
              <span dangerouslySetInnerHTML={{ __html: scenario.action.label }} />
              <ArrowRight className="size-4 ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </Link>
        </div>
      )}

      {scenario.tone === "danger" && (
        <div className="mt-7 flex items-start gap-3 px-4 py-3 bg-red-50 border-l-2 border-red-500 rounded-r-md text-caption text-red-800 max-w-xl leading-relaxed">
          <AlertCircle className="size-4 mt-0.5 shrink-0" />
          Veriff returned a non-verifiable result. Most retries succeed on the second attempt.
        </div>
      )}

      {/* Helper context */}
      <div className="mt-14 grid sm:grid-cols-2 gap-px bg-line border border-line rounded-xl overflow-hidden max-w-xl">
        <HelperCard
          icon={<Clock className="size-4 text-brand-700" />}
          title="Typical timing"
          body="Application review: 1&ndash;2 business days. KYC: under 10 minutes. Bank capture: 2 minutes."
        />
        <HelperCard
          icon={<HelpCircle className="size-4 text-brand-700" />}
          title="Need help?"
          body="Email <span class='text-ink font-medium'>support@gcbc.com</span> &mdash; we respond within one business day."
        />
      </div>
    </OnboardingShell>
  );
}

function HelperCard({ icon, title, body }) {
  return (
    <div className="bg-surface p-5">
      <div className="flex items-center gap-2">{icon}<h3 className="text-h4 text-ink font-semibold">{title}</h3></div>
      <p className="mt-2 text-caption text-ink-muted leading-relaxed" dangerouslySetInnerHTML={{ __html: body }} />
    </div>
  );
}
